import * as React from 'react';
import { ValidationIssueGroup } from '@insio/shared-types';
import { dedupeIssues } from '@insio/validation';
import { cn, getIssueExpected, severityStyle } from '../../utils';
import { ApplyExpectedButton, ErrorEntry } from '../../components/errors';
import type { ValidationIssue } from '../../types';

export interface FieldErrorListProps {
  issues: ValidationIssue[] | undefined;
  /** Text alignment of each row. */
  align?: 'left' | 'right';
  className?: string;
  /** When set, an apply arrow is shown next to any issue carrying
   * `meta.expected`; clicking it fills the field with that value. */
  onApplyExpected?: (expected: string) => void;
  /** aria-label / title for the apply button. */
  applyExpectedLabel?: string;
}

/**
 * Severity-coloured inline issue list, rendered directly under a form field.
 * Returns `null` when there are no issues so callers can drop it in
 * unconditionally.
 *
 * Issues sharing the same `code` (or `message` when code is missing) are
 * collapsed via `dedupeIssues` so the same sentence is not shown twice —
 * once as a warning and once as an error — on a single field. The path is
 * implicitly identical here, so collapsing by `code` is enough.
 *
 * Each row delegates to `ErrorEntry` so `meta.expected` (and `meta.actual`)
 * are surfaced consistently with the rest of the toolkit.
 */
export const FieldErrorList: React.FC<FieldErrorListProps> = ({
  issues,
  align = 'left',
  className,
  onApplyExpected,
  applyExpectedLabel
}) => {
  const visibleIssues = React.useMemo(
    () =>
      issues?.length
        ? dedupeIssues(issues, i =>
            // AI findings carry a model-authored message — never collapse them
            // with the rule issue sharing their code.
            i.groups?.includes(ValidationIssueGroup.AI_ANALYSIS)
              ? `${i.code}|${i.message}`
              : i.code || i.message
          )
        : [],
    [issues]
  );
  if (!visibleIssues.length) return null;
  return (
    <ul className={cn('mt-2xs space-y-2xs', className)}>
      {visibleIssues.map((issue, i) => {
        const s = severityStyle(issue.severity);
        const expected = getIssueExpected(issue);
        return (
          <li
            key={i}
            className={cn(
              'text-xs leading-tight',
              align === 'right' ? 'text-right' : 'text-left',
              s?.iconColor
            )}
          >
            <ErrorEntry issue={issue} />
            {onApplyExpected && expected != null && (
              <ApplyExpectedButton
                onApply={() => onApplyExpected(expected)}
                label={applyExpectedLabel ?? ''}
              />
            )}
          </li>
        );
      })}
    </ul>
  );
};

FieldErrorList.displayName = 'FieldErrorList';
